import moment from 'moment'
import { translate } from '~/lib/translate'
import type { TypedSchemaError } from 'vee-validate'
import {
  type DateInput,
  formatDate as formatDateValue,
  formatDateTime as formatDateTimeValue,
  formatTime as formatTimeValue,
  parseDateInput,
  resolveDatetimeLocale,
} from '~/lib/datetime'
import { TASK_STATUS, ACTION_LIST_STATUS, USER_MODELS } from '~/ts/enum'
import { NOTIFICATION_TYPE } from '~/ts/enum'

import {
  type AvailabilityType,
  type Gender,
  OrganisationType,
  type OrganisationTypeOption,
  type Organization,
  type RelationshipStatus,
  type ScaleOption,
  SchedulerType,
  type SchedulerTypeOption,
  type Weekday,
} from '~/ts/globals'
import { getAuthCookie } from '~/utils/auth-cookie'
import { LanguageEnum } from '~/ts/enum'
import {
  CATEGORY_CHIP_CLASS,
  DEADLINE_BASE,
  DEADLINE_TONE_CLASS,
  STATUS_PILL_BASE,
  STATUS_TONE_CLASS,
  glyphSvg,
  resolveDeadline,
  resolveStatus,
} from '~/lib/statusTokens'

const CANONICAL_APP_NAME = 'inCTRL'

export function canonicalAppName(value: string | undefined): string {
  const trimmed = value?.trim()
  if (!trimmed) return CANONICAL_APP_NAME

  return trimmed.toLowerCase() === CANONICAL_APP_NAME.toLowerCase() ? CANONICAL_APP_NAME : trimmed
}

export const APP_NAME: string = canonicalAppName(import.meta.env.VITE_APP_NAME)
export const BROADCAST_URL: string = import.meta.env.VITE_BROADCAST_URL
export const API_URL: string = import.meta.env.VITE_API_URL
export const TICKET_API_URL: string = import.meta.env.VITE_TICKET_API_URL

export const defaultCountryId: number = 73

export const apiUrl = () => {
  const url = new URL(import.meta.env.VITE_API_URL)
  return url.origin
}

export const AUTH_HEADER = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getAuthCookie()}`,
}

export const formatMoney = (value: number, lang: string = 'nl-NL'): string => {
  return new Intl.NumberFormat(lang, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(value)
}

export const formatNumber = (
  value: number | string | null | undefined,
  lang: string = 'nl-NL',
): string => {
  const num = typeof value === 'string' ? Number(value) : value
  if (num === null || num === undefined || Number.isNaN(num)) {
    return value == null ? '' : String(value)
  }
  return new Intl.NumberFormat(lang).format(num)
}

export const secondsToMinutes = (data: number): number => {
  return Math.floor(data / 60)
}

// `Intl` needs a region-qualified locale. vue-i18n's `locale.value` is the bare
// 12/25/2026 and 02:30 PM. Both locales the portal supports are day-first and
// 24-hour (epic #3379), so resolve to en-GB / nl-NL before touching Intl.
export const toDateLocale = (locale?: string | null): string => resolveDatetimeLocale(locale ?? '')

export const formatDate = (date: DateInput): string => formatDateValue(date)

export const formatDateTime = (datetime: DateInput): string => formatDateTimeValue(datetime)

export const formatDateTimeOrDate = (value: DateInput): string => {
  const parsed = parseDateInput(value)
  if (!parsed) {
    return ''
  }
  const isMidnight =
    parsed.getHours() === 0 && parsed.getMinutes() === 0 && parsed.getSeconds() === 0
  return isMidnight ? formatDateValue(parsed) : formatDateTimeValue(parsed)
}

export const sortIcons = {
  sort: 'fa-sort',
  sortAsc: 'fa-sort-up',
  sortDesc: 'fa-sort-down',
}

export const GENDER: Gender[] = [
  { name: 'Male', value: 'M' },
  { name: 'Female', value: 'F' },
  { name: 'Unknown', value: 'U' },
]

export const WorkFlow = {
  absenceWorkFlow: 'Absence WorkFlow Category',
}

export const Insurance = {
  illness_INSURER_CASE: 'Self-Insurer in case of Illness',
  wga_INSURER_CASE: 'Self-Insurer in case of WGA',
  absence_INSURER_CASE: 'Absence Insurance',
}

export const renderGender = (gender: string): string => {
  if (gender === 'M') {
    return translate('Male')
  } else if (gender === 'F') {
    return translate('Female')
  } else {
    return translate('Unknown')
  }
}

export function chunkArray<T>(array: T[], size: number): T[][] {
  const result: T[][] = []
  for (let i = 0; i < array.length; i += size) {
    const subArray: T[] = array.slice(i, i + size)
    result.push(subArray)
  }
  return result
}

export const transformErrors = <T>(
  errors: TypedSchemaError[],
): Partial<Record<keyof T, string>> => {
  const errorObj: Partial<Record<keyof T, string>> = {}
  for (const [index, error] of errors.entries()) {
    if (error.path) {
      errorObj[error.path as keyof T] = error.errors[index]
    }
  }
  return errorObj
}
export const VANGNET = [
  'Pregnance/Maternity Leave',
  'Illness due to Pregnancy',
  'Illness due to Delivery',
]
export const WAZO = ['Pregnancy/Maternity Leave']

export const parseNumber = (value: number | string | null): number | null => {
  if (value === null || value === '') {
    return null
  }
  return typeof value === 'string' ? parseFloat(value) : value
}

export const scaleOptions: ScaleOption[] = [
  { value: '01:00:00', name: '1 Hour' },
  { value: '00:30:00', name: '30 Minutes' },
  { value: '00:15:00', name: '15 Minutes' },
  { value: '00:10:00', name: '10 Minutes' },
  { value: '00:05:00', name: '5 Minutes' },
]

export const schedulerTypes: SchedulerTypeOption[] = [
  { name: 'Person', value: SchedulerType.Person },
  { name: 'Organisation', value: SchedulerType.Organisation },
]

export const organisationTypes: OrganisationTypeOption[] = [
  { name: 'Client', value: OrganisationType.Client },
  { name: 'Employer', value: OrganisationType.Employer },
  { name: 'Division', value: OrganisationType.Division },
  { name: 'Department', value: OrganisationType.Department },
]
export const availabilities: AvailabilityType = {
  available: true,
  unavailable: true,
  holiday: true,
}

export const formatTime = (datetime: DateInput): string => formatTimeValue(datetime)

export const statusPillHtml = (state: string, label?: string): string => {
  const descriptor = resolveStatus(state)

  return `<span class="${STATUS_PILL_BASE} ${STATUS_TONE_CLASS[descriptor.tone]}">${glyphSvg(
    descriptor.glyph,
  )}<span>${translate(label ?? descriptor.label)}</span></span>`
}

export const categoryChipHtml = (label: string): string =>
  `<span class="${CATEGORY_CHIP_CLASS}">${label}</span>`

export const badge = (data: string): string => {
  return categoryChipHtml(data)
}

export const notificationBadge = (data: string): string => {
  const LABELS: Record<string, string> = {
    [NOTIFICATION_TYPE.NOTIFICATION]: 'Notification',
    [NOTIFICATION_TYPE.IN_APP_NOTIFICATION]: 'In-app notification',
    [NOTIFICATION_TYPE.ALERT]: 'Alert',
  }
  const humanize = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

  const key = String(data)
  const label = LABELS[key] ?? humanize(key)
  return categoryChipHtml(label)
}

export const html = (html: string): string => {
  return `<div class="text-sm leading-6">${html}</div>`
}

export const renderConcluded = (data: Date): string => {
  return data
    ? `<div class="text-center">${statusPillHtml('concluded')}</div>`
    : `<div class="text-center">-</div>`
}

export const calculateProgress = (deadline: Date, status?: string | null): string => {
  const deadlineDate = moment(deadline, 'YYYY-MM-DD')
  const currentDate = moment(moment().format('YYYY-MM-DD'), 'YYYY-MM-DD')
  const descriptor = resolveDeadline(deadlineDate.diff(currentDate, 'days'), status)

  const icon = descriptor.tone === 'closed' ? '' : glyphSvg(descriptor.glyph)

  return `<span class="${DEADLINE_BASE} ${DEADLINE_TONE_CLASS[descriptor.tone]}">${icon}<span>${
    descriptor.label
  }</span></span>`
}

export const weekdays: Weekday[] = [
  {
    name: 'sun',
    title: 'sunday',
    value: 0,
  },
  {
    name: 'mon',
    title: 'monday',
    value: 1,
  },
  {
    name: 'tue',
    title: 'tuesday',
    value: 2,
  },
  {
    name: 'wed',
    title: 'wednesday',
    value: 3,
  },
  {
    name: 'thur',
    title: 'thursday',
    value: 4,
  },
  {
    name: 'fri',
    title: 'friday',
    value: 5,
  },
  {
    name: 'sat',
    title: 'saturday',
    value: 6,
  },
]

export const getUserNameInitials = (name?: string) => {
  if (!name) return 'U'
  const [firstName, lastName] = name.split(' ')
  return (firstName ? firstName.charAt(0) : '') + (lastName ? lastName.charAt(0) : '')
}

export const formatDateTimeToTime = (datetime: DateInput): string => formatTimeValue(datetime)

export const link = (url?: string): string => {
  if (!url) return ''
  const safe = encodeURI(url)
  return `
    <a href="${safe}"
       target="_blank"
       rel="noopener noreferrer"
       class="inline-flex items-center text-blue-600 hover:underline">
      <span class="mr-2">🔗</span>
      <span class="text-sm">view</span>
    </a>
  `
}

export const renderTaskListStatus = (status: string): string =>
  statusPillHtml(Object.values(TASK_STATUS).includes(status as TASK_STATUS) ? status : 'unknown')

export const renderActionListStatus = (status: string): string =>
  statusPillHtml(
    Object.values(ACTION_LIST_STATUS).includes(status as ACTION_LIST_STATUS) ? status : 'unknown',
  )

/** The untranslated task type, for styling; render the label with `renderTaskType`. */
export const taskTypeKey = (
  questionnaireType: string | null | undefined,
  hasCertificate = false,
): '' | 'Form' | 'Survey' | 'Template' | 'Certificate' => {
  if (questionnaireType === 'App\\Models\\Form\\Form') {
    return 'Form'
  } else if (questionnaireType === 'App\\Models\\Surveys\\SurveyQuestion') {
    return 'Survey'
  } else if (questionnaireType === 'App\\Models\\Template') {
    return 'Template'
  }
  // A certificate-only task carries no questionnaire (questionnaire_type is null)
  // but holds a certificate template — surface it so the type is never blank.
  if (hasCertificate) {
    return 'Certificate'
  }
  return ''
}

export const renderTaskType = (
  questionnaireType: string | null | undefined,
  hasCertificate = false,
): string => translate(taskTypeKey(questionnaireType, hasCertificate))

export const capitalize = (value: string): string => {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

export const languageOptions = [
  { name: 'English', value: LanguageEnum.EN },
  { name: 'Dutch', value: LanguageEnum.NL },
]

export const activityTypeList = [
  { id: 'person', name: 'Person' },
  { id: 'organisation', name: 'Organisation' },
]

export const activityProgressList = [
  { id: 'on_time', name: 'On Time' },
  { id: 'warning', name: 'Warning' },
  { id: 'overdue', name: 'Overdue' },
]

export const activityAppointmentTypes = [
  { value: 'on_site', name: 'On Site' },
  { value: 'phone_call', name: 'Phone Call' },
  { value: 'video_call', name: 'Video Call' },
]

export const scheduleTypes = [
  { value: 'contractorActivity', name: 'Contract' },
  { value: 'webshopActivity', name: 'Webshop' },
]

export const renderRecordType = (recordType: string): string => {
  return recordType
}

export const mainDashboardOrder = [
  'client_count',
  'employer_count',
  'division_count',
  'department_count',
  'person_count',
  'current_absentees_count',
  'action_list_count',
  'activity_list_count',
  'invoiced_activity_list_count',
  'documents_count',
  'pending_tasks_count',
  'hidden',
]
export interface RecentItemEntityConfig {
  path: string
  queryKey: string
  icon: string
  typeLabel: string
}

export const recentItemEntityConfig: Record<string, RecentItemEntityConfig> = {
  document: {
    path: '/documents',
    queryKey: 'documentId',
    icon: 'lucide:file-text',
    typeLabel: 'Document',
  },
  person: { path: '/persons', queryKey: 'personId', icon: 'lucide:user', typeLabel: 'Person' },
  client: { path: '/clients', queryKey: 'clientId', icon: 'lucide:briefcase', typeLabel: 'Client' },
  employer: {
    path: '/employers',
    queryKey: 'employerId',
    icon: 'lucide:building-2',
    typeLabel: 'Employer',
  },
  division: {
    path: '/divisions',
    queryKey: 'divisionId',
    icon: 'lucide:git-branch',
    typeLabel: 'Division',
  },
  department: {
    path: '/departments',
    queryKey: 'departmentId',
    icon: 'lucide:users',
    typeLabel: 'Department',
  },
  documents: { path: '/documents', queryKey: '', icon: 'lucide:folder', typeLabel: 'Documents' },
}

export const organizationNamedata = (obj: Organization): string => {
  if (obj?.organization_name) {
    return obj?.organization_name
  } else if (obj?.name) {
    return obj?.name
  } else if (obj?.user_type === 'department') {
    return obj?.department_name
  } else if (obj?.user_type === 'division') {
    return obj?.division_name
  }
  return ''
}

export const getOrganizationLabelData = (obj: Organization): string => {
  if (obj.user_type === 'person') {
    return 'Person'
  } else if (obj.user_type) {
    return 'Organization'
  }
  return ''
}

export const renderPortal = (model: string): string => {
  switch (model) {
    case USER_MODELS.PERSON:
      return translate('Person Portal')
    case USER_MODELS.PROFESSIONAL:
      return translate('Professional Portal')
    case USER_MODELS.EMPLOYER:
      return translate('Employer Portal')
    case USER_MODELS.ADMIN:
      return translate('Admin Portal')
    default:
      return translate('System')
  }
}

export const fileAccessTypes = [
  { value: 'person', name: 'Person' },
  { value: 'employment', name: 'Employment' },
  { value: 'absence-history', name: 'Absence History' },
  { value: 'activity-action-list', name: 'Action List' },
]

export const portals = [
  { value: 'App\\Models\\Users\\AdminUser', name: 'Admin Portal' },
  { value: 'App\\Models\\Users\\FileUser', name: 'Person Portal' },
  { value: 'App\\Models\\Users\\Professional', name: 'Professional Portal' },
  { value: 'App\\Models\\Users\\EmployerUser', name: 'Employer Portal' },
]

export const RELATIONSHIP_STATUSES: RelationshipStatus[] = [
  // EMR translates a bare 'Single' as "Enkel"; the context key keeps "Alleenstaand" (#5000).
  { value: 'single', name: 'Single (relationship status)' },
  { value: 'in_a_relationship', name: 'In a Relationship' },
  { value: 'married', name: 'Married' },
  { value: 'civil_partnership', name: 'Civil Partnership' },
  { value: 'cohabiting', name: 'Cohabiting' },
  { value: 'separated', name: 'Separated' },
  { value: 'divorced', name: 'Divorced' },
  { value: 'widowed', name: 'Widowed' },
  { value: 'engaged', name: 'Engaged' },
  { value: 'prefer_not_to_say', name: 'Prefer Not To Say' },
  { value: 'other', name: 'Other' },
]

export const DATE_RANGE_SHORTCUTS = [
  {
    text: 'Last 30 Days',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 30)
      return [start, end]
    },
  },
  {
    text: 'Last 2 Months',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 60)
      return [start, end]
    },
  },
  {
    text: 'Last 3 Months',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 90)
      return [start, end]
    },
  },
  {
    text: 'Last 6 Months',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 180)
      return [start, end]
    },
  },
  {
    text: 'Last 9 Months',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 270)
      return [start, end]
    },
  },
  {
    text: 'Last 1 Year',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 365)
      return [start, end]
    },
  },
]

export const formatFileSize = (bytes?: number): string => {
  if (bytes === undefined || bytes === null || isNaN(bytes) || bytes < 0) {
    return '0 B'
  }

  if (bytes === 0) return '0 B'

  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB']
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1)
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`
}

export const str = {
  join: (...parts: (string | number | null | undefined)[]) => ({
    delimiter: (delimiter: string = ' - '): string => {
      return parts
        .filter((part) => part !== null && part !== undefined && part !== '')
        .map((part) => String(part))
        .join(delimiter)
    },
  }),
}
